import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { PROJECT_STATUSES, statusMeta } from '../projectStatus';
import SalesIntelSection from './SalesIntelSection';
import { fmtDate } from '../timefmt';
export { PROJECT_STATUSES, statusMeta };

const fmtMoney = (n, cur) => (cur === 'USD' ? 'US$' : 'A$') + Math.round(n || 0).toLocaleString();
const fmtN = n => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DashboardView({ me, canFlags, toast, goView, executive }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (executive) return;
    api.dashboard().then(setData).catch(e => setErr(e.response?.data?.detail || e.message));
  }, [executive]);

  const firstName = ((me?.name || me?.email || '').split(/[@\s]/)[0] || 'there');

  if (executive) return (
    <div className="dash" data-testid="dashboard-view">
      <div className="dash-hello" data-testid="dash-exec-hello">
        <h2>Welcome back, {firstName}</h2>
        <p className="codegen-sub">Company-wide sales performance at a glance — quotes, orders, forecast and stale pipeline.</p>
      </div>
      <SalesIntelSection toast={toast} goView={goView}
        sections={{ overall: true, forecast: true, stale: true, items: true }} />
      <footer className="dash-easter" data-testid="dash-easter-egg">
        Dreamt and inspired by adoptive work parents, Lloyd &amp; Penny, created by Rafi
        <span className="dash-easter-tc" data-testid="dash-easter-tc">we love tc</span>
      </footer>
    </div>
  );

  if (err) return <div className="empty-note" style={{ margin: 30 }}>Dashboard failed to load: {err}</div>;
  if (!data) return <div className="empty-note" style={{ margin: 30 }}>Loading dashboard…</div>;

  const s = data.stats || {};

  return (
    <div className="dash" data-testid="dashboard-view">
      <div className="dash-hello">
        <h2>Welcome back, {firstName}</h2>
        <p className="codegen-sub">Here's what's happening across your quotes and projects.</p>
      </div>

      <div className="dash-stats">
        <button className="dash-stat" data-testid="dash-stat-quotes" onClick={() => goView('quotes', 'pricing')}>
          <span className="dash-num">{s.my_quotes}</span><span className="dash-lbl">Quotes you've made</span>
        </button>
        <button className="dash-stat" data-testid="dash-stat-projects" onClick={() => goView('projects')}>
          <span className="dash-num">{s.active_projects}</span><span className="dash-lbl">Active projects</span>
        </button>
        <button className="dash-stat" data-testid="dash-stat-myprojects" onClick={() => goView('projects')}>
          <span className="dash-num">{s.my_projects}</span><span className="dash-lbl">Projects total</span>
        </button>
      </div>

      <div className="dash-cols">
        <section className="dash-card" data-testid="dash-projects">
          <div className="dash-card-head">
            <h3>Active projects</h3>
            <button className="btn btn-sm" onClick={() => goView('projects')}>All projects →</button>
          </div>
          {(data.active_projects || []).length === 0
            ? <div className="empty-note">No active projects. Create one from the Projects page or save a quote.</div>
            : (data.active_projects || []).map(p => (
              <button key={p.id} className="dash-row" data-testid={'dash-proj-' + p.id} onClick={() => goView('projects')}>
                <div className="dash-row-main">
                  <strong>{p.name}</strong>
                  <span>{[p.client, p.items_count + ' datasheet(s)', p.missing_count ? p.missing_count + ' missing' : ''].filter(Boolean).join(' · ')}</span>
                </div>
                <span className={'proj-status-pill ' + statusMeta(p.status).cls}>{statusMeta(p.status).label}</span>
              </button>
            ))}
        </section>

        <section className="dash-card" data-testid="dash-recent-quotes">
          <div className="dash-card-head">
            <h3>Recent quotes</h3>
            <button className="btn btn-sm" onClick={() => goView('quotes', 'pricing')}>All quotes →</button>
          </div>
          {(data.recent_quotes || []).length === 0
            ? <div className="empty-note">No quotes yet — build one from the Quote Builder.</div>
            : (data.recent_quotes || []).map(q => (
              <button key={q.id} className="dash-row" data-testid={'dash-quote-' + q.id} onClick={() => goView('quotes', 'pricing')}>
                <div className="dash-row-main">
                  <strong>{q.opportunity}</strong>
                  <span>{[fmtDate(q.created_at), q.lines_count + ' item' + (q.lines_count !== 1 ? 's' : ''), q.ns_quote_number ? 'NS: ' + q.ns_quote_number : ''].filter(Boolean).join(' · ')}</span>
                </div>
                <span className="ps-muted">{fmtN(q.total)} {q.display_cur}</span>
              </button>
            ))}
        </section>
      </div>

      <PersonalIntel goView={goView} />

      {canFlags && (
        <section className="dash-card dash-flags" data-testid="dash-discrepancies">
          <div className="dash-card-head">
            <h3>System discrepancies <span className="dash-flag-tag">Master / Technical</span></h3>
          </div>
          {(data.discrepancies || []).length === 0
            ? <div className="dash-ok" data-testid="dash-no-flags">✓ No discrepancies found — everything looks consistent.</div>
            : (data.discrepancies || []).map(f => (
              <div className="dash-flag" key={f.key} data-testid={'dash-flag-' + f.key}>
                <div className="dash-flag-head">
                  <span className="dash-flag-count">{f.count}</span>
                  <strong>{f.label}</strong>
                </div>
                <div className="dash-flag-samples">{(f.samples || []).join(' · ')}{f.count > (f.samples || []).length ? ' …' : ''}</div>
                {f.hint && <div className="dash-flag-hint">{f.hint}</div>}
              </div>
            ))}
        </section>
      )}
      <footer className="dash-easter" data-testid="dash-easter-egg">
        Dreamt and inspired by adoptive work parents, Lloyd &amp; Penny, created by Rafi
        <span className="dash-easter-tc" data-testid="dash-easter-tc">we love tc</span>
      </footer>
    </div>
  );
}

const PERIODS = [['7', '7 days'], ['30', '30 days'], ['90', '3 months'], ['ytd', 'Year to date']];
const COLS = [['date', 'Date'], ['tranid', 'Number'], ['client', 'Client'], ['status', 'Status'], ['amount', 'Amount']];

function PersonalTx({ rows, cur, kind }) {
  const [sort, setSort] = useState({ key: 'date', dir: -1 });
  const sorted = [...rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    const c = typeof x === 'number' ? x - y : String(x || '').localeCompare(String(y || ''));
    return c * sort.dir;
  });
  const click = (key) => setSort(s => s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'amount' || key === 'date' ? -1 : 1 });
  if (!rows.length) return <div className="empty-note" data-testid={'dash-personal-' + kind + '-empty'}>No {kind === 'orders' ? 'sales orders' : 'quotes'} in this period.</div>;
  return (
    <div className="si-scroll" data-testid={'dash-personal-' + kind + '-table'}>
      <table className="si-table">
        <thead><tr>{COLS.map(([k, l]) => (
          <th key={k} className={'sortable' + (k === 'amount' ? ' num' : '') + (sort.key === k ? ' on' : '')} data-testid={'dash-personal-sort-' + k} onClick={() => click(k)}>
            {l}{sort.key === k ? (sort.dir < 0 ? ' ▾' : ' ▴') : ''}
          </th>))}</tr></thead>
        <tbody>{sorted.map((r, i) => (
          <tr key={r.tranid + '|' + r.date + '|' + i}>
            <td style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
            <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{r.tranid}</td>
            <td className="si-item-name" title={r.client}>{r.client}</td>
            <td>{r.status}</td>
            <td className="num strong">{fmtMoney(r.amount, cur)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function PersonalIntel() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [period, setPeriod] = useState('90');
  const [kind, setKind] = useState('orders');
  useEffect(() => {
    let live = true;
    setD(null);
    api.salesIntelPersonal(period).then(r => { if (live) setD(r); }).catch(e => { if (live) setErr(e.response?.data?.detail || e.message); });
    return () => { live = false; };
  }, [period]);
  if (err) return null;
  if (!d) return (
    <section className="dash-card si-card" data-testid="dash-personal-intel">
      <div className="empty-note">Loading your sales report…</div>
    </section>
  );
  if (!d.available) return (
    <section className="dash-card si-card" data-testid="dash-personal-intel">
      <div className="dash-card-head si-head"><h3>Your Sales Intelligence</h3></div>
      <div className="empty-note" data-testid="dash-personal-nomatch">{d.reason}</div>
    </section>
  );
  const cur = d.currency;
  const lbl = d.period?.label || '';
  return (
    <section className="dash-card si-card" data-testid="dash-personal-intel">
      <div className="dash-card-head si-head">
        <h3>Your Sales Intelligence <span className="dash-flag-tag si-tag" data-testid="dash-personal-rep">{d.rep}{d.auto_matched ? ' · auto-matched' : ''}</span></h3>
        <div className="si-period-row" data-testid="dash-personal-period">
          {PERIODS.map(([k, l]) => <button key={k} className={'si-period' + (period === k ? ' on' : '')} data-testid={'dash-personal-period-' + k} onClick={() => setPeriod(k)}>{l}</button>)}
        </div>
      </div>
      <div className="si-kpis" data-testid="dash-personal-kpis">
        <div className="si-kpi"><span className="si-kpi-num">{(d.pipeline?.count || 0).toLocaleString()}</span><span className="si-kpi-lbl">Open quotes (your pipeline)</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(d.pipeline?.amount, cur)}</span><span className="si-kpi-lbl">Pipeline value</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(d.orders?.amount, cur)}</span><span className="si-kpi-lbl">Your sales orders · {lbl} · {(d.orders?.count || 0).toLocaleString()}</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(d.quotes?.amount, cur)}</span><span className="si-kpi-lbl">Your quotes · {lbl} · {(d.quotes?.count || 0).toLocaleString()}</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{(d.won?.count || 0).toLocaleString()}</span><span className="si-kpi-lbl">Won · {lbl} · {fmtMoney(d.won?.amount, cur)}</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{(d.lost?.count || 0).toLocaleString()}</span><span className="si-kpi-lbl">Lost/expired · {lbl}</span></div>
      </div>
      <div className="si-panel" data-testid="dash-personal-tx">
        <div className="si-panel-head">
          <h4>Your transactions · {lbl}</h4>
          <div className="si-period-row">
            <button className={'si-period' + (kind === 'orders' ? ' on' : '')} data-testid="dash-personal-kind-orders" onClick={() => setKind('orders')}>Sales orders ({(d.order_rows || []).length})</button>
            <button className={'si-period' + (kind === 'quotes' ? ' on' : '')} data-testid="dash-personal-kind-quotes" onClick={() => setKind('quotes')}>Quotes ({(d.quote_rows || []).length})</button>
          </div>
        </div>
        <PersonalTx rows={kind === 'orders' ? (d.order_rows || []) : (d.quote_rows || [])} cur={cur} kind={kind} />
      </div>
      <div className="si-grid">
        {(d.stale?.items || []).length > 0 && (
          <div className="si-panel" data-testid="dash-personal-stale">
            <h4>Your stale open quotes (30+ days idle{d.stale.count > 5 ? ' · showing oldest 5 of ' + d.stale.count : ''})</h4>
            <table className="si-table"><tbody>
              {d.stale.items.map(sq => (
                <tr key={sq.tranid}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{sq.tranid}</td>
                  <td className="si-item-name" title={sq.client}>{sq.client}</td>
                  <td className="num">{sq.idle_days}d idle</td>
                  <td className="num strong">{fmtMoney(sq.amount, cur)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
        {(d.top_items || []).length > 0 && (
          <div className="si-panel" data-testid="dash-personal-items">
            <h4>Your most quoted items · {lbl}</h4>
            <table className="si-table"><tbody>
              {d.top_items.map(it => (
                <tr key={it.itemid}>
                  <td className="si-item-name" title={it.itemid}>{it.itemid}</td>
                  <td className="num">{it.qty.toLocaleString()}</td>
                  <td className="num strong">{fmtMoney(it.amount, cur)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </section>
  );
}
